import { useEffect, useState, useCallback, useRef, useContext } from 'react';
import Welcome from '../../components/Welcome';
import Attire from '../../components/Attire';
import ConferenceInfo from '../../components/ConferenceInfo';
import Agenda from '../../components/Agenda';
import FAQ from '../../components/FAQ';
import Tourism from '../../components/Tourism';
import LinkToExpo from '../../components/LinkToExpo';
import { deliveryClientABC } from '../../api/cmsDeliveryClient';
import { ABCContextData } from '../../layouts/header/ABCLayout';
import { UserContext } from '../../common/UserContext';
import { useOutletContext } from "react-router-dom";
import './ABCDetails.css'
import { HeaderLayoutDetail } from '../../models';
import { Tabs } from '../../utils/tabs';
import { AccessCodeConfig, getAccessCodeMappings, getConfInfoGroupForCode, getFaqGroupForCode, getAgendaVisibilityForCode, getRoleNameForCode } from '../../utils/accessCodeMappings';

type currentSectionDetail = {
  codename: string,
  updateStateWithData: Function
}

const ABCDetails = () => {
  const { setActiveTab, headerData, selectedTab, containerRef } = useOutletContext<ABCContextData>();
  const [welcomePageData, setWelcomePageData] = useState<any>(null);
  const [attirePageData, setAttirePageData] = useState<any>(null);
  const [agendaPageData, setAgendaPageData] = useState<any>(null);
  const [faqPageData, setFaqPageData] = useState<any>(null);
  const [linkToExpoPageData, setLinkToExpoPageData] = useState<any>(null);
  const [confInfoPageData, setCOnfInfoPageData] = useState<any>(null);
  const [tourismPageData, setTourismPageData] = useState<any>(null);
  const [displayLinkToExpo, setDisplayLinkToExpo] = useState(true);
  const sectionsRef = useRef<HTMLDivElement[]>([]);
  const userData = useContext(UserContext);
  const [accessCodeMap, setAccessCodeMap] = useState<Map<string, AccessCodeConfig>>(new Map());

  // Fetch access code → visibility group mappings from CMS (once)
  useEffect(() => {
    getAccessCodeMappings(deliveryClientABC).then(setAccessCodeMap);
  }, []);

  useEffect(() => {
    // Gets three items by their codenames. The codenames are unique per project.
    deliveryClientABC.items()
      .depthParameter(2)
      .inFilter("system.type", ["welcome_page"])
      .toPromise()
      .then((response) => {
        const welcomeData = response.data.items.filter(w => w.system.type === "welcome_page");
        setWelcomePageData(welcomeData);
      })
  }, []);

  useEffect(() => {
    if (sectionsRef && sectionsRef.current) {
      sectionsRef.current.forEach((s) => {
        if (s.id === selectedTab) {
          s.scrollIntoView({ behavior: 'smooth' })
          setActiveTab(selectedTab);
          if (selectedTab === "welcome") {
            containerRef.current?.scrollIntoView({ behavior: 'smooth' })
          }
        }
      })
    }
  }, [selectedTab, linkToExpoPageData, tourismPageData, faqPageData])

  useEffect(() => {
    if (headerData) {
      let headerDetails = headerData as HeaderLayoutDetail[];
      let displayLnkToExpo = false;
      headerDetails.forEach((e) => {
        if (e.elements.tablist.linkedItems.some((t) => t.elements.id.value === "linktoexpo")) {
          displayLnkToExpo = true;
        }
      })
      setDisplayLinkToExpo(displayLnkToExpo);
    }
  }, [headerData])

  useEffect(() => {
    // Observer for activating tabs
    const options = {
      root: null,
      threshold: 0.3
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveTab(entry.target.id);
        }
      });
    }, options);

    sectionsRef.current.forEach((section) => {
      observer.observe(section);
    });

    // To disable the entire IntersectionObserver
    return () => {
      observer.disconnect();
    };
  },[]);

  useEffect(() => {
    Tabs.forEach((t) => {
      if (checkIfTabToDisplay(t.id)) {
        loadData(t.id);
      }
    })
  }, [headerData])

  const refCallback = useCallback((element: never) => {
    if (element) {
      sectionsRef.current.push(element);
    }
  },[]);

  const checkIfTabToDisplay = (tb: string) => {
    let isTabExist: boolean = false;
    if (headerData) {
      headerData.forEach((e: any) => {
        if (e.elements.tablist.linkedItems.some((a: any) => a.elements.id.value === tb)) {
          isTabExist = true;
          return;
        }
      });
    }
    return isTabExist;
  }

  const loadData = (sectionToLoadData: string) => {
    let sectionDetail: currentSectionDetail = {
      codename: "",
      updateStateWithData: () => { }
    }
    if (sectionToLoadData) {
      switch (sectionToLoadData.toLowerCase()) {
        case "welcome":
          sectionDetail.codename = "attire_page"
          sectionDetail.updateStateWithData = setAttirePageData;
          break;
        case "attire":
          sectionDetail.codename = "conference_information"
          sectionDetail.updateStateWithData = setCOnfInfoPageData;
          break;
        case "confinfo":
          sectionDetail.codename = "agenda_page"
          sectionDetail.updateStateWithData = setAgendaPageData;
          break;
        case "agenda":
          sectionDetail.codename = "faq_page"
          sectionDetail.updateStateWithData = setFaqPageData;
          break;
        case "faq":
          sectionDetail.codename = "tourism_page"
          sectionDetail.updateStateWithData = setTourismPageData;
          break;
        case "eventarea":
          sectionDetail.codename = "link_to_expo_page"
          sectionDetail.updateStateWithData = setLinkToExpoPageData;
          break;
      }
    }

    loadDataFromApi(sectionDetail);
  }

  // CMS-driven: lookup access code → conf info visibility group from access_code_mapping
  const getPolicyCodeForConfInfo = () => {
    if (userData.userData && userData.userData.accessCode) {
      const isDealerFromApi = userData?.userData?.isDealer ?? false;
      return getConfInfoGroupForCode(accessCodeMap, userData.userData.accessCode, isDealerFromApi);
    }
    return "general";
  }

  // CMS-driven: lookup access code → FAQ visibility group from access_code_mapping
  const getPolicyCodeForFAQ = () => {
    if (userData.userData && userData.userData.accessCode) {
      return getFaqGroupForCode(accessCodeMap, userData.userData.accessCode);
    }
    return "general";
  }

  // CMS-driven: lookup access code → role name for item-level visibility
  const getRoleName = () => {
    if (userData.userData && userData.userData.accessCode) {
      const isDealerFromApi = userData?.userData?.isDealer ?? false;
      return getRoleNameForCode(accessCodeMap, userData.userData.accessCode, isDealerFromApi);
    }
    return "";
  }

  const loadDataFromApi = (sectionDetail: currentSectionDetail) => {
    if (sectionDetail.codename === "agenda_page") {
      let code = "agenda_page_item";
      // CMS-driven agenda visibility — no hardcoded access codes.
      // Retailer numbers (e.g. "123456") won't be in the CMS map;
      // the utility falls back to API's isDealer flag automatically.
      const currentAccessCode = userData?.userData?.accessCode?.toLowerCase() ?? '';
      const isDealerFromApi = userData?.userData?.isDealer ?? false;
      const currentConfig = accessCodeMap.get(currentAccessCode);
      const isDealerCode = currentConfig?.isDealerCode ?? false;
      // Agenda visibility: from CMS mapping, or fallback via isDealer from API
      const visibility = getAgendaVisibilityForCode(accessCodeMap, currentAccessCode, isDealerFromApi);
      if (isDealerFromApi || isDealerCode) {
        code = "agenda_page_item_dealer";
      }

      // Filter agenda links by the 'visible' multiple_choice element
      // Reg_type: show links for user reg type only
      const filterAgendaLinks = (linkedItems: any[], vis: string): any[] => {
        return linkedItems.filter((link: any) => {
          const opts = (link.elements?.visible?.value || []).map((v: any) => v.codename);
          if (vis === 'lpc') return opts.includes('visible_to_lpc');
          if (vis === 'dealer') return opts.includes('visible_to_dealer');
          if (vis === 'nondealer') return opts.includes('visible_to_nondealer');
          return true;
        });
      };

      // For LPC: try visibilitygroup taxonomy first, fallback to codename + lpc link filtering
      if (visibility === 'lpc') {
        deliveryClientABC.items()
          .depthParameter(2)
          .containsFilter('elements.visibilitygroup', ['lpc'])
          .toPromise()
          .then((response: any) => {
            let item = response.data.items?.[0];
            if (item) {
              if (item.elements?.links_and_pdfs?.linkedItems) {
                item.elements.links_and_pdfs.linkedItems = filterAgendaLinks(
                  item.elements.links_and_pdfs.linkedItems, visibility
                );
              }
              return item;
            }
            // Fallback: fetch by codename and collect lpc-tagged links from all pages
            return deliveryClientABC.items()
              .depthParameter(2)
              .equalsFilter('system.codename', code)
              .toPromise()
              .then((codeResp: any) => {
                let baseItem = codeResp.data.items?.[0];
                return deliveryClientABC.items()
                  .depthParameter(2)
                  .inFilter('system.type', ['agenda_page'])
                  .toPromise()
                  .then((allResp: any) => {
                    const allItems = allResp.data.items || [];
                    if (baseItem) {
                      const allLinks = allItems.flatMap((p: any) => p.elements?.links_and_pdfs?.linkedItems || []);
                      const seen = new Set();
                      const uniqueLinks = allLinks.filter((l: any) => {
                        if (seen.has(l.system.codename)) return false;
                        seen.add(l.system.codename);
                        return true;
                      });
                      baseItem.elements.links_and_pdfs.linkedItems = filterAgendaLinks(uniqueLinks, visibility);
                    }
                    return baseItem;
                  });
              });
          })
          .then((item: any) => sectionDetail.updateStateWithData(item))
          .catch(() => sectionDetail.updateStateWithData(undefined));
      } else {
        deliveryClientABC.items()
          .depthParameter(2)
          .equalsFilter('system.codename', code)
          .toPromise()
          .then((response: any) => {
            let item = response.data.items?.[0];
            // dealer/nondealer: filter links on the fetched page
            if (item?.elements?.links_and_pdfs?.linkedItems) {
              item.elements.links_and_pdfs.linkedItems = filterAgendaLinks(
                item.elements.links_and_pdfs.linkedItems, visibility
              );
            }
            return item;
          })
          .then((item: any) => sectionDetail.updateStateWithData(item))
          .catch(() => sectionDetail.updateStateWithData(undefined));
      }
    }
    else if (sectionDetail.codename === "conference_information") {
      let code = getPolicyCodeForConfInfo();
      deliveryClientABC.items()
        .type("conference_information")
        .depthParameter(2)
        .containsFilter("elements.reggroup", [code])
        .toPromise()
        .then((response) => {
          sectionDetail.updateStateWithData(response.data.items[0]);
        })
    }
    else if (sectionDetail.codename === "faq_page") {
      let code = getPolicyCodeForFAQ();
      deliveryClientABC.items()
        .type("faq_page")
        .depthParameter(2)
        .containsFilter("elements.reggroupfaq", [code])
        .toPromise()
        .then((response) => {
          sectionDetail.updateStateWithData(response.data.items[0]);
        })
    }
    else {
        deliveryClientABC.items()
        .depthParameter(2)
        .inFilter("system.type", [sectionDetail.codename])
        .toPromise()
        .then((response) => {
          sectionDetail.updateStateWithData(response.data.items[0]);
        })
    }
  }

  return <>
    <section id="welcome" ref={refCallback}>
      <Welcome welcomePageData={welcomePageData ? welcomePageData[0] : null} />
    </section>
    <section id="confinfo" ref={refCallback}>
      <ConferenceInfo conferenceInfoPageData={confInfoPageData} confInfoGroup={getPolicyCodeForConfInfo()} roleName={getRoleName()} />
    </section>
    <section id="agenda" ref={refCallback}>
      <Agenda agendaPageData={agendaPageData} />
    </section>
    <section id="attire" ref={refCallback}>
      <Attire attirePageData={attirePageData}></Attire>
    </section>
    <section id="faq" ref={refCallback}>
      <FAQ faqPageData={faqPageData} roleName={getRoleName()} />
    </section>
    <section id="eventarea" ref={refCallback}>
      <Tourism tourismPageData={tourismPageData} title={"Nashville area"} />
    </section>
    <section id="linktoexpo" className={`${displayLinkToExpo ? "" : "hidelinktoexpo"}`} ref={refCallback}>
      <LinkToExpo linkToExpoPageData={linkToExpoPageData} />
    </section>
  </>
}

export default ABCDetails;